import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import Layout from '../Layout';
import RegisterLink from '../Helper/RegisterLink';
import LoadingOverlay from '../Helper/LoadingOverlay';
import GoogleLoginButton from '../SocialAccounts/GoogleLoginButton';
import FacebookLoginButton from '../SocialAccounts/FacebookLoginButton';
import TrimbleLoginButton from '../SocialAccounts/TrimbleLoginButton';
import CgCaptcha from '../Recaptcha/CgCaptcha';
import { CaptchaTypes } from '../../constants';
import { TRIMBLE_STYLE } from '../Helper/constants';
import { CorporateLoginForm } from './CorporateLoginForm';
import { PasswordForm } from './PasswordForm';
import { EmailForm } from './EmailForm';
import { TOTPForm } from './TOTPForm';

export const SocialLoginButtons = withTranslation()(({ showTrimble, trimbleStyle, t }) => {
  return (
    <>
      {(window.FACEBOOK_LOGIN || window.GOOGLE_LOGIN || showTrimble) && <span className="divider">{t('or')}</span>}
      <div className="social-login-buttons">
        {window.GOOGLE_LOGIN && (
          <div className="row">
            <GoogleLoginButton id="btn-google-login" label="Continue with Google" />
          </div>
        )}
        {window.FACEBOOK_LOGIN && (
          <div className="row">
            <FacebookLoginButton id="btn-facebook-login" label="Continue with Facebook" />
          </div>
        )}
        {showTrimble && (
          <div className="row">
            <TrimbleLoginButton
              id="gtm-login-continue-with-trimble"
              buttonStyle={trimbleStyle}
              label="Continue with Trimble"
              trimbleActivationFlow={false}
              autoRegister
            />
          </div>
        )}
      </div>
    </>
  );
});

const getPageTitle = (returnTo) => {
  if (returnTo.includes('try.')) {
    return 'Sign in to get your trial';
  }

  if (returnTo.includes(window.STORE_ADDR) || returnTo.includes('doCheckout')) {
    return 'Sign in to complete your purchase';
  }

  if (returnTo.includes('demo.')) {
    return 'Sign in to activate the Corona trial on your account';
  }

  return 'Sign in';
};

const Login = ({
  onSubmitLogin,
  onSubmitEmail,
  onSubmitTOTP,
  onInputChange,
  onReCaptchaValidate,
  onSubmitCorporateLogin,
  onRequestRegularLogin,
  onResetLogin,
  socialLogin,
  data,
  app,
  routing,
  generateCgCaptcha,
  resend,
  resendActivationCode,
}) => {
  return (
    <Layout
      className="login"
      cover={{ src: '/images/cover-main.png' }}
      reCaptchaValidateHandler={data.captchaType === CaptchaTypes.RECAPTCHA ? onReCaptchaValidate : null}
    >
      <LoginForm
        onSubmitEmail={onSubmitEmail}
        onSubmitLogin={onSubmitLogin}
        onSubmitTOTP={onSubmitTOTP}
        onInputChange={onInputChange}
        socialLogin={socialLogin}
        onSubmitCorporateLogin={onSubmitCorporateLogin}
        onRequestRegularLogin={onRequestRegularLogin}
        onResetLogin={onResetLogin}
        data={data}
        app={app}
        routing={routing}
        generateCgCaptcha={generateCgCaptcha}
        resend={resend}
        resendActivationCode={resendActivationCode}
      />
    </Layout>
  );
};

const LoginForm = withTranslation()(
  ({
    onSubmitLogin,
    onInputChange,
    onSubmitEmail,
    onSubmitCorporateLogin,
    onRequestRegularLogin,
    onResetLogin,
    onSubmitTOTP,
    generateCgCaptcha,
    socialLogin,
    reCaptcha,
    data,
    app,
    routing,
    resend,
    resendActivationCode,
    t,
  }) => {
    const buttonsDisabled = app.loading || app.redirecting;

    // Show custom page title for users that are redirected from the website's trial page
    const { return_to = '' } = routing.query || {};
    const pageTitle = t(getPageTitle(return_to));
    const error = data.error || socialLogin.error;
    const { requestPassword, allowCorporateLogin, requestTOTP } = data;

    const renderContent = () => {
      if (allowCorporateLogin) {
        return (
          <CorporateLoginForm
            email={data.email}
            fieldErrors={data.fieldErrors}
            buttonsDisabled={buttonsDisabled}
            onInputChange={onInputChange}
            onSubmitCorporateLogin={onSubmitCorporateLogin}
            onChangeLoginEmail={onResetLogin}
            onRequestRegularLogin={onRequestRegularLogin}
          />
        );
      }

      if (requestPassword) {
        return (
          <PasswordForm
            email={data.email}
            password={data.password}
            error={error}
            fieldErrors={data.fieldErrors}
            fieldWarnings={data.fieldWarnings}
            buttonsDisabled={buttonsDisabled}
            resend={resend}
            resendActivationCode={resendActivationCode}
            onInputChange={onInputChange}
            onChangeLoginEmail={onResetLogin}
            onSubmitLogin={() => onSubmitLogin(reCaptcha)}
          >
            {data.captchaType === CaptchaTypes.CG_CAPTCHA && (
              <CgCaptcha
                value={data.cgcaptcha.captchaValue}
                imageUrl={data.cgcaptcha.imageUrl}
                domain={window.CG_CAPTCHA_DOMAIN}
                loading={data.cgcaptcha.loading}
                onInputChange={(e) => {
                  e.preventDefault();
                  onInputChange('cgCaptchaValue', e.target.value);
                }}
                onCaptchaRefresh={(e) => {
                  e.preventDefault();
                  generateCgCaptcha();
                }}
              />
            )}
            {data.fieldErrors.captcha && <div className="captcha-error">{data.fieldErrors.captcha}</div>}
          </PasswordForm>
        );
      }

      return (
        <>
          <EmailForm
            email={data.email}
            fieldErrors={data.fieldErrors}
            buttonsDisabled={buttonsDisabled}
            onInputChange={onInputChange}
            onSubmitEmail={onSubmitEmail}
          />
          <SocialLoginButtons showTrimble={window.TRIMBLE_LOGIN} trimbleStyle={TRIMBLE_STYLE.LIGHT} />
        </>
      );
    };

    if (requestTOTP) {
      return <TOTPForm onSubmit={onSubmitTOTP} onBack={onResetLogin} loading={data.loading} error={error} />;
    }

    return (
      <div className="wrapper-shrink">
        <h1 className="title">{pageTitle}</h1>
        <section className="login-page-subtitle">
          <p>{t('One Chaos account. All Chaos services.')}</p>
          <p>
            {t(
              'Your former CGarchitect login, existing Enscape, Anima, or Veras login now works as your Chaos account.',
            )}
          </p>
        </section>

        <LoadingOverlay active={data.loading || socialLogin.loading || app.redirecting} />
        {renderContent()}
        <p className="signup-info-text">
          {t('New user?')} <RegisterLink id="link-register">{t('Create Account')}</RegisterLink>
        </p>
      </div>
    );
  },
);

Login.propTypes = {
  onSubmitLogin: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object.isRequired,
};

Login.propTypes = {
  onSubmitLogin: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object.isRequired,
};

export default connect(
  (state) => ({
    data: state.login,
    socialLogin: state.socialLogin,
    resend: state.activationResend,
    app: state.app,
    routing: state.routing,
  }),
  (dispatch) => ({
    onInputChange: (id, value) => dispatch({ type: 'LOGIN_INPUT_CHANGE', data: { id, value } }),
    onSubmitLogin: (reCaptcha) => dispatch({ type: 'SUBMIT_LOGIN', reCaptcha }),
    onSubmitEmail: () => dispatch({ type: 'SUBMIT_LOGIN_EMAIL' }),
    onSubmitTOTP: (totpCode, rememberDevice) => dispatch({ type: 'SUBMIT_TOTP', totpCode, rememberDevice }),
    onReCaptchaValidate: (value) => dispatch({ type: 'SUBMIT_LOGIN#VALIDATE_RECAPTCHA', data: { value } }),
    generateCgCaptcha: () => dispatch({ type: 'GENERATE_CG_LOGIN_CAPTCHA' }),
    onSubmitCorporateLogin: () => dispatch({ type: 'SUBMIT_CORPORATE_LOGIN' }),
    onResetLogin: () => dispatch({ type: 'LOGIN_RESET' }),
    onRequestRegularLogin: () => dispatch({ type: 'REQUEST_REGULAR_LOGIN' }),
    resendActivationCode: (email) => dispatch({ type: 'RESEND_ACTIVATION_CODE', data: { email } }),
  }),
)(Login);
